import { colorToRGBA } from "src/utils/colorToRgba";
import React, { useMemo, useState } from "react";
import { Color, Flex, Scroller, Search, Spinner } from "src/elements";
import styled from "styled-components";

import { SelectOption } from "../store/types";
import { Option } from "./Option";

const DropdownElement = styled(Flex)`
  width: 100%;
  max-width: 400px;
  flex-direction: column;
  background-color: ${Color.white};
  border: 1px solid ${Color.primary};
  border-radius: 3px;
  overflow: hidden;
  user-select: none;
  box-shadow: 0px 0px 7px ${colorToRGBA(Color.primary, 0.3)};

  input {
    border: none;
  }
`;

const DropdownDivider = styled(Flex)`
  height: 1px;
  width: calc(100% - 20px);
  background-color: ${Color.lightGray};
  margin: 0 10px;
`;

interface Props<T> {
  value: T;
  options: SelectOption<T>[];
  name: string;
  isSearchable?: boolean;
  isLoading: boolean;
  size?: "small";
  onSelect: (value: T) => void;
  closeDropdown: () => void;
  pinnedValues?: T[];
  onTogglePin?: (value: T) => void;
  getPinActionLabel?: (label: string, isPinned: boolean) => string;
}

export const Dropdown = <T,>({
  value,
  options,
  name,
  isSearchable = false,
  isLoading,
  size,
  onSelect,
  closeDropdown,
  pinnedValues,
  onTogglePin,
  getPinActionLabel,
}: Props<T>) => {
  const [search, setSearch] = useState<string>("");

  const filteredOptions = useMemo(() => {
    // Selected option first, then pinned options, then the rest.
    const rank = (option: SelectOption<T>) =>
      option.value === value ? 0 : pinnedValues?.includes(option.value) ? 1 : 2;

    return options
      .filter((option) => !search || option.label.toLowerCase().includes(search.toLowerCase()))
      .sort((option1, option2) => rank(option1) - rank(option2));
  }, [options, search, value, pinnedValues]);

  const onClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  return (
    <DropdownElement onClick={onClick} data-testid={`inner-${name}`}>
      {isSearchable && (
        <>
          <Search
            search={search}
            setSearch={setSearch}
            isBorderless
            isFocusedAutomatically
            rightIcon={!search && { name: "triangleUp", onClick: closeDropdown }}
          />
          <DropdownDivider />
        </>
      )}

      {!isLoading && (
        <Scroller size="thin" maxHeight="200px" gutter="auto">
          <Flex column role="listbox" aria-label={name}>
            {filteredOptions.map((option, i) => {
              const firstUnsearchableOption = i === 0 && !isSearchable;
              const icon =
                option.icon ?? (firstUnsearchableOption ? { name: "triangleUp" } : undefined);
              const key = `${option.label}-${String(option.value)}`;
              const isPinned = !!pinnedValues?.includes(option.value);

              return (
                <Option
                  data-testid={`${name}-option`}
                  key={key}
                  label={option.label}
                  value={option.value}
                  isDisabled={option.isDisabled}
                  icon={icon}
                  isSelected={value === option.value}
                  onSelect={onSelect}
                  size={size}
                  pin={
                    onTogglePin &&
                    getPinActionLabel && {
                      isPinned,
                      actionLabel: getPinActionLabel(option.label, isPinned),
                      onToggle: () => onTogglePin(option.value),
                    }
                  }
                />
              );
            })}
          </Flex>

          {filteredOptions.length === 0 && <Option label="No Results..." isSelectable={false} />}
        </Scroller>
      )}

      {isLoading && (
        <Flex padding="10px" justify="center">
          <Spinner size="medium" />
        </Flex>
      )}
    </DropdownElement>
  );
};
